import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { LOGO_URL } from "../lib/api";
import { LayoutDashboard, Package, FolderTree, ShoppingBag, Undo2, Mail, MessageSquare, Heart, Users, Settings, ShoppingCart, Ticket, Megaphone, LogOut, Menu, X } from "lucide-react";

const ITEMS = [
  { to: "/admin", end: true, label: "Tableau de bord", icon: LayoutDashboard },
  { to: "/admin/produits", label: "Produits", icon: Package },
  { to: "/admin/categories", label: "Catégories", icon: FolderTree },
  { to: "/admin/commandes", label: "Commandes", icon: ShoppingBag },
  { to: "/admin/paniers-abandonnes", label: "Paniers abandonnés", icon: ShoppingCart },
  { to: "/admin/retours", label: "Retours", icon: Undo2 },
  { to: "/admin/messages", label: "Messages", icon: MessageSquare, badge: "Nouveau" },
  { to: "/admin/remerciement", label: "Remerciement", icon: Heart, badge: "Nouveau" },
  { to: "/admin/promo", label: "Codes promo", icon: Ticket, badge: "Nouveau" },
  { to: "/admin/bannieres", label: "Bannières", icon: Megaphone, badge: "Nouveau" },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/clients", label: "Clientes", icon: Users },
  { to: "/admin/parametres", label: "Paramètres", icon: Settings },
];

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/admin/login", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (loading) return <div className="p-12">…</div>;
  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen md:flex bg-berulia-beige">
      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-white border-b border-berulia-border px-4 h-14">
        <Link to="/admin" className="flex items-center gap-2">
          <img src={LOGO_URL} className="h-7 w-auto" alt="BERULIA"/>
          <span className="overline text-berulia-rosedeep">Back-office</span>
        </Link>
        <button
          onClick={() => setOpen(true)}
          aria-label="Ouvrir le menu"
          data-testid="admin-menu-open"
          className="p-2 text-berulia-ink"
        >
          <Menu size={22}/>
        </button>
      </header>

      {/* Backdrop (mobile only, when open) */}
      {open && (
        <button
          aria-label="Fermer le menu"
          onClick={() => setOpen(false)}
          className="md:hidden fixed inset-0 z-40 bg-black/40"
        />
      )}

      {/* Sidebar — drawer on mobile, static on md+ */}
      <aside
        className={`bg-white border-r border-berulia-border flex flex-col
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200
          ${open ? "translate-x-0" : "-translate-x-full"}
          md:static md:translate-x-0 md:transform-none md:transition-none md:w-64 md:z-auto`}
      >
        <div className="flex items-center justify-between px-6 py-6 border-b border-berulia-border">
          <Link to="/admin" onClick={() => setOpen(false)}>
            <img src={LOGO_URL} className="h-10 w-auto" alt="BERULIA"/>
            <p className="overline text-berulia-rosedeep mt-2">Back-office</p>
          </Link>
          <button
            onClick={() => setOpen(false)}
            aria-label="Fermer le menu"
            className="md:hidden p-1 text-berulia-inkmuted"
          >
            <X size={20}/>
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {ITEMS.map(i => (
            <NavLink key={i.to} to={i.to} end={i.end} data-testid={`adminnav-${i.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({isActive}) => `flex items-center gap-3 px-3 py-2 text-sm transition ${isActive ? "bg-berulia-rose text-berulia-ink" : "text-berulia-inkmuted hover:bg-berulia-beigewarm hover:text-berulia-ink"}`}>
              <i.icon size={16}/>
              <span className="flex-1">{i.label}</span>
              {i.badge && (
                <span className="text-[9px] tracking-widest uppercase bg-berulia-rosedeep text-white px-1.5 py-0.5 rounded-sm">
                  {i.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <button onClick={()=>{logout(); navigate("/admin/login");}} data-testid="admin-logout" className="m-4 flex items-center gap-2 px-3 py-2 text-sm text-berulia-inkmuted hover:text-berulia-ink"><LogOut size={14}/>Se déconnecter</button>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet/>
      </main>
    </div>
  );
}
